import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  CheckCircleIcon,
  ClockIcon,
  TruckIcon,
  XCircleIcon,
  CurrencyDollarIcon,
  MapPinIcon,
  CalendarIcon
} from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckCircleIconSolid } from '@heroicons/react/24/solid';

const OrderTracking = ({ orderId, orderNumber, showTitle = true }) => {
  const [tracking, setTracking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchTracking = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        
        let url;
        if (orderId) {
          // Authenticated tracking by ID
          url = `/api/orders/${orderId}/tracking`;
        } else if (orderNumber) {
          // Public tracking by order number
          url = `/api/orders/track/${orderNumber}`;
        } else {
          throw new Error('Either orderId or orderNumber is required');
        }

        const response = await axios.get(url, {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });

        if (response.data.success) {
          setTracking(response.data.data);
        }
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to load tracking information');
      } finally {
        setLoading(false);
      }
    };

    if (orderId || orderNumber) {
      fetchTracking();
    }
  }, [orderId, orderNumber]);

  const getStatusIcon = (status) => {
    const iconClass = "w-6 h-6";
    switch (status) {
      case 'pending':
        return <ClockIcon className={iconClass} />;
      case 'processing':
        return <ClockIcon className={iconClass} />;
      case 'shipped':
        return <TruckIcon className={iconClass} />;
      case 'delivered':
        return <CheckCircleIconSolid className={iconClass} />;
      case 'cancelled':
        return <XCircleIcon className={iconClass} />;
      case 'refunded':
        return <CurrencyDollarIcon className={iconClass} />;
      default:
        return <ClockIcon className={iconClass} />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'gray';
      case 'processing':
        return 'blue';
      case 'shipped':
        return 'indigo';
      case 'delivered':
        return 'green';
      case 'cancelled':
        return 'red';
      case 'refunded':
        return 'yellow';
      default:
        return 'gray';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('he-IL', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-24 bg-gray-200 rounded-lg"></div>
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-gray-200 rounded-full"></div>
              <div className="flex-1 space-y-2">
                <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                <div className="h-3 bg-gray-200 rounded w-1/2"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex items-center">
          <XCircleIcon className="w-5 h-5 text-red-500 ml-2" />
          <p className="text-red-700">{error}</p>
        </div>
      </div>
    );
  }

  if (!tracking) {
    return null;
  }

  const { order, currentStatus, timeline } = tracking;
  const orderInfo = order || tracking;

  return (
    <div className="space-y-6">
      {/* Header */}
      {showTitle && (
        <div className="border-b pb-4">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">מעקב הזמנה</h2>
          <div className="flex items-center justify-between text-sm text-gray-600">
            <span>מספר הזמנה: <span className="font-semibold">{orderInfo.orderNumber || orderInfo.order_number}</span></span>
            {orderInfo.tracking_number && (
              <span>מספר מעקב: <span className="font-semibold">{orderInfo.tracking_number}</span></span>
            )}
          </div>
        </div>
      )}

      {/* Current Status Card */}
      <div className={`bg-${getStatusColor(orderInfo.status)}-50 border-2 border-${getStatusColor(orderInfo.status)}-200 rounded-lg p-6`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4 space-x-reverse">
            <div className={`w-16 h-16 bg-${getStatusColor(orderInfo.status)}-100 rounded-full flex items-center justify-center text-${getStatusColor(orderInfo.status)}-600`}>
              {getStatusIcon(orderInfo.status)}
            </div>
            <div>
              <h3 className="text-xl font-bold text-gray-900">
                {currentStatus?.label || orderInfo.status}
              </h3>
              <p className="text-gray-600 mt-1">
                {currentStatus?.description || 'ההזמנה בתהליך'}
              </p>
            </div>
          </div>
        </div>

        {/* Delivery Info */}
        {(orderInfo.estimated_delivery || orderInfo.delivery_date) && (
          <div className="mt-4 pt-4 border-t border-gray-200 flex items-center justify-between">
            {orderInfo.estimated_delivery && orderInfo.status !== 'delivered' && (
              <div className="flex items-center text-gray-700">
                <CalendarIcon className="w-5 h-5 ml-2" />
                <span>זמן אספקה משוער: <span className="font-semibold">{formatDate(orderInfo.estimated_delivery)}</span></span>
              </div>
            )}
            {orderInfo.delivery_date && (
              <div className="flex items-center text-green-700">
                <CheckCircleIconSolid className="w-5 h-5 ml-2" />
                <span>נמסר בתאריך: <span className="font-semibold">{formatDate(orderInfo.delivery_date)}</span></span>
              </div>
            )}
          </div>
        )}

        {tracking.shippingAddress && (
          <div className="mt-3 flex items-start text-gray-700">
            <MapPinIcon className="w-5 h-5 ml-2 mt-0.5 flex-shrink-0" />
            <span>{tracking.shippingAddress.street}, {tracking.shippingAddress.city}</span>
          </div>
        )}
      </div>

      {/* Timeline */}
      {timeline && timeline.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">היסטוריית מעקב</h3>
          <div className="space-y-0">
            {timeline.map((item, index) => {
              const statusColor = getStatusColor(item.status);
              const isLast = index === timeline.length - 1;
              
              return (
                <div key={item.id || index} className="relative flex items-start pb-8">
                  {/* Timeline Line */}
                  {!isLast && (
                    <div className={`absolute top-12 right-6 w-0.5 h-full bg-${statusColor}-200`}></div>
                  )}

                  {/* Timeline Dot */}
                  <div className={`relative z-10 flex-shrink-0 w-12 h-12 bg-${statusColor}-100 border-4 border-${statusColor}-300 rounded-full flex items-center justify-center text-${statusColor}-600`}>
                    {getStatusIcon(item.status)}
                  </div>

                  {/* Content */}
                  <div className="mr-4 flex-1 bg-white rounded-lg border border-gray-200 p-4 shadow-sm hover:shadow-md transition-shadow">
                    <div className="flex items-start justify-between mb-2">
                      <h4 className="font-semibold text-gray-900">
                        {item.statusInfo?.label || item.status}
                      </h4>
                      <span className="text-sm text-gray-500">
                        {formatDate(item.timestamp)}
                      </span>
                    </div>

                    {item.description && (
                      <p className="text-gray-700 mb-2">{item.description}</p>
                    )}

                    {item.location && (
                      <div className="flex items-center text-sm text-gray-600">
                        <MapPinIcon className="w-4 h-4 ml-1" />
                        <span>{item.location}</span>
                      </div>
                    )}

                    {item.updatedBy && (
                      <div className="mt-2 text-xs text-gray-500">
                        עודכן על ידי: {item.updatedBy.name}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderTracking;
